/**
 * SpeedDial component for mobile navigation
 * Provides quick access to search, outlook switcher, table of contents, and download functionality
 */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Backdrop,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
} from '@mui/material';
import { Search, TextSnippet, List } from '@mui/icons-material';

import { usePrint } from '../../providers/PrintProvider';
import HexaIcon from './HexaIcon';

/**
 * SpeedDial with tooltip for mobile navigation
 *
 * @param {Object} props - Component props
 * @param {string} props.code - Entity code
 * @param {string} props.nom - Entity name
 * @param {Function} props.handleTableOfContentDialogOpen - Handler for table of contents dialog
 * @param {Function} props.handleSearchDialogOpen - Handler for search dialog
 * @param {Function} props.setOpenOutlookDialog - Handler for outlook dialog
 * @param {Function} props.handlePrintDialog - Handler for print dialog (optional)
 * @returns {JSX.Element} SpeedDial component
 */
const SpeedDialWithTooltip = ({
  code: _code,
  nom: _nom,
  handleTableOfContentDialogOpen,
  handleSearchDialogOpen,
  setOpenOutlookDialog,
  handlePrintDialog,
}) => {
  const [open, setOpen] = useState(false);
  const _outlook = useSelector((state) => state.outlook.value);
  const { openPrintDialog } = usePrint();

  /**
   * Handle SpeedDial open
   * @param {Event} event - Event object
   * @param {string} reason - Reason for opening
   */
  const handleOpen = () => {
    setOpen(true);
  };

  /**
   * Handle SpeedDial close
   * @param {Event} event - Event object
   * @param {string} reason - Reason for closing
   */
  const handleClose = () => {
    setOpen(false);
  };

  // Define speed dial actions
  const actions = [
    {
      icon: <Search />,
      tooltipTitle: 'Communes',
      onClick: () => {
        setOpen(false);
        handleSearchDialogOpen?.();
      },
    },
    {
      icon: <HexaIcon />,
      tooltipTitle: 'Horizons',
      onClick: () => {
        setOpen(false);
        setOpenOutlookDialog?.(true);
      },
    },
    {
      icon: <List />,
      tooltipTitle: 'Indicateurs',
      onClick: () => {
        setOpen(false);
        handleTableOfContentDialogOpen?.();
      },
    },
    {
      icon: <TextSnippet />,
      tooltipTitle: 'Téléchargement',
      onClick: () => {
        setOpen(false);
        // Use custom handler if provided, fallback to default print dialog
        if (handlePrintDialog) {
          handlePrintDialog();
        } else {
          openPrintDialog?.();
        }
      },
    },
  ];

  return (
    <Box
      sx={{
        displayPrint: 'none',
        position: 'fixed',
        top: 60,
        right: 15,
        zIndex: 2,
      }}
      className="speedDialWithTooltip"
    >
      <Backdrop open={open} sx={{ pointerEvents: 'none' }} />
      <SpeedDial
        ariaLabel="Navigation mobile"
        icon={<SpeedDialIcon />}
        onClose={handleClose}
        onClick={handleOpen}
        open={open}
        direction="down"
        FabProps={{ size: 'medium' }}
      >
        {actions.map(({ tooltipTitle, ...actionProps }) => (
          <SpeedDialAction
            key={tooltipTitle}
            tooltipTitle={tooltipTitle}
            tooltipOpen
            {...actionProps}
          />
        ))}
      </SpeedDial>
    </Box>
  );
};

SpeedDialWithTooltip.displayName = 'SpeedDialWithTooltip';

export default SpeedDialWithTooltip;
